import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Spinner,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { useSetRecoilState, useRecoilState } from "recoil";
import { refreshGlobal } from "../../../../atoms/refreshGlobal";
import TitleFlex from "../../vendasDigitadas/defaultComponents/titleFlex";
import {
  CNPJFormater,
  CPFFormater,
} from "../../../../defaultFunctions/formaters";
import { dateForList } from "../../../../constants/dataFormat";
import copy from "copy-to-clipboard";
import { goToVendaBolePixListagem } from "../../../../routes/coordinator";
import { customColors } from "../../../../atoms/customColors";
import { toast } from "react-toastify";
import VendasBoletoIndividualApi from "../../../../services/VendasBoletoIndividuaApi";

interface ModalRevisãoVendaBoletoProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  objeto: any;
  response?: any;
  setResponseToModal: (response: any) => void;
  setBoleto: (boleto: any) => void;
  initialValues: any;
  setLoadingState: (loading: boolean) => void;
}

export default function ModalRevisãoVendaBoleto({
  isOpen,
  setIsOpen,
  objeto,
  response,
  setResponseToModal,
  setBoleto,
  initialValues,
  setLoadingState,
}: ModalRevisãoVendaBoletoProps) {
  const finalRef = React.useRef(null);
  const navigate = useNavigate();
  const setRefreshGlobal = useSetRecoilState(refreshGlobal);
  const refresh = useRecoilState(refreshGlobal)[0];
  const colors = useRecoilState(customColors)[0];
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [localLoadingState, setLocalLoadingState] = useState(false);

  const onClose = () => {
    setIsOpen(false);
    setIsConfirmed(false);
    setResponseToModal(null);
    goToVendaBolePixListagem(navigate);
  };

  const handleConfirm = async () => {
    setLocalLoadingState(true);
    setLoadingState(true);
    try {
      const response = await VendasBoletoIndividualApi.postVendaBolePix(objeto);
      setResponseToModal(response);
      setBoleto(initialValues);
      setIsConfirmed(true);
      toast.success("Boleto criado com sucesso!");
    } catch (error: any) {
      if (error.response && error.response.data && error.response.data._error) {
        const { message, reasons } = error.response.data._error.error;
        const errorTranslations: { [key: string]: string } = {
          "Late fee mode is not valid": "O modo de multa está inválido",
          "Late fee start date must be greater than the expiration date":
            "A data de início da multa deve ser maior que a data de vencimento",
          "Interest mode is not valid": "O modo de juros está inválido",
          "Invalid value fields": "Campos de valor inválidos",
          "Amount is more than limit": "O valor excede o limite permitido",
        };
        const translatedReasons = reasons.map(
          (reason: string) => errorTranslations[reason] || reason
        );
        toast.error(
          `Erro ao gerar o boleto: ${message}. Motivos: ${translatedReasons.join(
            "; "
          )}`
        );
      } else {
        const errorMessage =
          error.response?.data?.message || JSON.stringify(error);
        toast.error(errorMessage);
      }
    } finally {
      setLocalLoadingState(false);
      setLoadingState(false);
    }
  };

  return (
    <Modal
      finalFocusRef={finalRef}
      isOpen={isOpen}
      onClose={onClose}
      size={"2xl"}
    >
      <ModalOverlay backdropFilter="blur(10px)" />
      <ModalContent>
        <ModalHeader>
          <Flex w={"100%"} justifyContent={"center"}>
            <Text fontWeight={"bold"} color={colors.GREENMONEDD}>
              {isConfirmed
                ? "Venda gerada com sucesso!"
                : "Revisar Dados do Boleto"}
            </Text>
          </Flex>
        </ModalHeader>
        <ModalBody>
          {objeto && (
            <>
              <TitleFlex item={"Dados do Boleto"} />
              <Flex flexDir={"column"} px={4}>
                <Text>
                  Valor de Cobrança:{" "}
                  {objeto.valor
                    ? objeto.valor.toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      })
                    : "R$ 0,00"}
                </Text>
                <Text>
                  Data de Vencimento:{" "}
                  {moment(objeto.dataVencimento).format(dateForList)}
                </Text>
                {isConfirmed && response && (
                  <>
                    <Text fontSize={"16px"} fontWeight={"bold"}>
                      {response.barcode}
                    </Text>
                    <Text fontSize={"12px"}>Código de Barras</Text>
                    <Text>Id da Transação: {response.id}</Text>
                    <Text fontSize={"16px"} fontWeight={"bold"}>
                      {response.emv}
                    </Text>
                    <Text fontSize={"12px"}>EMV (Pix Copia e Cola)</Text>
                  </>
                )}
              </Flex>

              <TitleFlex item={"Dados do Pagador"} />
              <Flex flexDir={"column"} px={4}>
                <Text>Nome do Pagador: {objeto.nomePagador}</Text>
                <Text>
                  Documento do Pagador:{" "}
                  {objeto.cpfPagador && objeto.cpfPagador.length === 11
                    ? CPFFormater(objeto.cpfPagador)
                    : CNPJFormater(objeto.cpfPagador)}
                </Text>
                <Text>Email: {objeto.emailPagador}</Text>
              </Flex>

              <TitleFlex item={"Endereço de Cobrança"} />
              <Flex flexDir={"column"} px={4}>
                <Text fontSize={"13px"}>
                  {`${objeto.enderRua}, ${objeto.enderNumero}/${objeto.enderBairro}-${objeto.enderCidade}-${objeto.enderEstado}`}
                </Text>
                <Text>CEP: {objeto.enderCEP}</Text>
              </Flex>

              {objeto.permitirRegras && (
                <>
                  <TitleFlex item={"Regras de Juros e Multa"} />
                  <Flex flexDir={"column"} px={4}>
                    <Text>
                      Juros:{" "}
                      {objeto.RegrasJuros.mode === "SEM_JUROS"
                        ? "Sem Juros"
                        : `${
                            objeto.RegrasJuros.mode === "DAILY_AMOUNT"
                              ? "Valor Diário"
                              : objeto.RegrasJuros.mode === "DAILY_PERCENTAGE"
                              ? "Percentual Diário"
                              : "Percentual Mensal"
                          }: ${
                            objeto.RegrasJuros.amount ||
                            objeto.RegrasJuros.percentage
                          }`}
                    </Text>
                    <Text>
                      Multa:{" "}
                      {objeto.RegrasMulta.mode === "SEM_MULTA"
                        ? "Sem Multa"
                        : `${
                            objeto.RegrasMulta.mode === "FIXED"
                              ? "Valor Fixo"
                              : "Percentual"
                          }: ${
                            objeto.RegrasMulta.amount ||
                            objeto.RegrasMulta.percentage
                          }`}
                    </Text>
                  </Flex>
                </>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter justifyContent={"space-evenly"}>
          {!isConfirmed ? (
            <>
              <Button
                background={colors.GRAY}
                color={"white"}
                mr={3}
                onClick={onClose}
              >
                Cancelar
              </Button>
              <Button
                bgColor={colors.BLUEMONEDDBUTTONS}
                color={colors.WHITE}
                isDisabled={localLoadingState}
                onClick={handleConfirm}
              >
                {localLoadingState ? <Spinner size="sm" mr={2} /> : null}
                Confirmar Criação
              </Button>
            </>
          ) : (
            <>
              <Button
                background={colors.GRAY}
                color={"white"}
                mr={3}
                onClick={onClose}
              >
                Fechar
              </Button>
              {response && (
                <>
                  <Button
                    bgColor={colors.VARIANT}
                    color={colors.WHITE}
                    mr={3}
                    onClick={() => {
                      copy(response.barcode ? response.barcode : "");
                      toast.success("Código de barras copiado!");
                    }}
                  >
                    Copiar Código de Barras
                  </Button>
                  <Button
                    bgColor={colors.VARIANT}
                    color={colors.WHITE}
                    mr={3}
                    onClick={() => {
                      copy(response.emv ? response.emv : "");
                      toast.success("Pix Copia e Cola copiado!");
                    }}
                  >
                    Copiar Pix
                  </Button>
                  <Flex flexDir={"column"} px={4} alignItems={"center"}>
                    <a
                      href={`https://m2admin.softkuka.com.br/bolepix/view/${response.id}`}
                      target={"_blank"}
                      rel="noopener noreferrer"
                    >
                      <Button color={colors.WHITE} bgColor={colors.VARIANT}>
                        Abrir Boleto
                      </Button>
                    </a>
                  </Flex>
                </>
              )}
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
